import React from "react"

/**
 * ShapeBorder component renders decorative shape borders at the bottom of sections.
 * These are only available when using a background image layout.
 *
 * @param {string} type - The shape type: 'wave', 'point', 'rounded', 'slant', 'point_up_opacity'
 * @param {string} color - The fill color for the shape (typically white to blend with next section)
 * @param {string} className - Additional CSS classes
 * @param {boolean} isMobile - Whether to render mobile-optimized version
 */
const ShapeBorder = ({ type = "wave", color = "#FFFFFF", className = "", isMobile = false }) => {
  const height = isMobile ? 24 : 96
  const viewBoxHeight = isMobile ? 24 : 96

  const renderShape = () => {
    switch (type) {
      case "wave":
        return (
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 1440 ${viewBoxHeight}`}
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={{ display: "block", marginBottom: "-1px" }}>
            <path
              d={
                isMobile
                  ? "M0 24V12C120 4 240 0 360 0C480 0 600 8 720 12C840 16 960 16 1080 12C1200 8 1320 4 1380 2L1440 0V24H0Z"
                  : "M0 96V48C120 16 240 0 360 0C480 0 600 32 720 48C840 64 960 64 1080 48C1200 32 1320 16 1380 8L1440 0V96H0Z"
              }
              fill={color}
            />
          </svg>
        )

      case "point":
        return (
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 1440 ${viewBoxHeight}`}
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={{ display: "block", marginBottom: "-1px" }}>
            <path d={isMobile ? "M0 0L720 24L1440 0L1440 24H0V0Z" : "M0 0L720 96L1440 0L1440 96H0V0Z"} fill={color} />
          </svg>
        )

      case "rounded": {
        const roundedHeight = isMobile ? 27 : 107
        return (
          <svg
            width="100%"
            height={roundedHeight}
            viewBox={`0 0 1440 ${roundedHeight}`}
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={{ display: "block", marginBottom: "-1px" }}>
            <path
              d={
                isMobile
                  ? "M1440 0C1440 0 1172 26.379 720 26.379C268 26.379 0 0 0 0L0 27H1440L1440 0Z"
                  : "M1440 0C1440 0 1172 105.516 720 105.516C268 105.516 0 0 0 0L0 107H1440L1440 0Z"
              }
              fill={color}
            />
          </svg>
        )
      }

      case "slant":
        return (
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 1440 ${viewBoxHeight}`}
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={{ display: "block", marginBottom: "-1px" }}>
            <path d={isMobile ? "M0 24L1440 0V24H0Z" : "M0 96L1440 0V96H0Z"} fill={color} />
          </svg>
        )

      case "point_up_opacity": {
        // Height is 160px for desktop, 40px for mobile (half)
        const pointUpHeight = isMobile ? 40 : 160
        return (
          <svg
            width="100%"
            height={pointUpHeight}
            viewBox={`0 0 1440 ${pointUpHeight}`}
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={{ display: "block", marginBottom: "-1px" }}>
            {/* Semi-transparent trapezoid - sides extend higher, middle at extraHeight */}
            <path
              d={isMobile ? "M0 0L720 7.5L1440 0L1440 40L0 40Z" : "M0 0L720 30L1440 0L1440 160L0 160Z"}
              fill={color}
              fillOpacity="0.25"
            />
            {/* Solid white triangle - pointing UP from bottom */}
            <path d={isMobile ? "M0 40L720 7.5L1440 40H0Z" : "M0 160L720 30L1440 160H0Z"} fill={color} />
          </svg>
        )
      }

      default:
        return null
    }
  }

  return renderShape()
}

/**
 * Shape border options for the editor selector
 */
export const SHAPE_BORDER_OPTIONS = [
  { id: "none", name: "None", description: "No shape border" },
  { id: "wave", name: "Wave", description: "Smooth curved wave pattern" },
  { id: "point", name: "Point", description: "Triangle pointing downward" },
  { id: "rounded", name: "Rounded", description: "Curved arc shape" },
  { id: "slant", name: "Slant", description: "Diagonal line" },
  { id: "point_up_opacity", name: "Point Up Opacity", description: "Triangle pointing up with transparency" }
]

/**
 * Layouts that support shape borders (full-width background image layouts)
 */
export const LAYOUTS_WITH_SHAPE_BORDER_SUPPORT = ["full_image", "contained_image"]

/**
 * Helper function to determine if a layout supports shape borders
 * @param {string} layout - The layout type
 * @returns {boolean} - Whether the layout supports shape borders
 */
export const layoutSupportsShapeBorder = (layout) => LAYOUTS_WITH_SHAPE_BORDER_SUPPORT.includes(layout)

/**
 * Helper function to determine if a layout type has a shape border (legacy support)
 * @param {string} layout - The layout type
 * @returns {boolean} - Whether the layout has a shape border
 */
export const hasShapeBorder = (layout) => {
  const shapeBorderLayouts = [
    "full_image_wave",
    "full_image_point",
    "full_image_rounded",
    "full_image_slant",
    "full_image_point_up_opacity"
  ]
  return shapeBorderLayouts.includes(layout)
}

/**
 * Helper function to extract the shape type from layout (legacy support)
 * @param {string} layout - The layout type (e.g., "full_image_wave")
 * @returns {string|null} - The shape type (e.g., "wave") or null if no shape
 */
export const getShapeTypeFromLayout = (layout) => {
  const shapeMapping = {
    full_image_wave: "wave",
    full_image_point: "point",
    full_image_rounded: "rounded",
    full_image_slant: "slant",
    full_image_point_up_opacity: "point_up_opacity"
  }
  return shapeMapping[layout] || null
}

/**
 * Helper function to get the base layout from a shape border layout (legacy support)
 * @param {string} layout - The layout type (e.g., "full_image_wave")
 * @returns {string} - The base layout (e.g., "full_image")
 */
export const getBaseLayoutFromShapeLayout = (layout) => {
  if (hasShapeBorder(layout)) {
    return "full_image"
  }
  return layout
}

export default ShapeBorder
